// netResilience.ts — shared RU-fallback + progressive-timeout policy (web).
//
// CORE-449. Generalises the ad-hoc RU fallback that used to live only in
// apiFallback.ts (REST) and StreamChatContext (chat) so REST, SignalR and
// Stream can share ONE notion of "RU is the better path right now" and one
// progressive-timeout ladder.
//
// Design (see the CORE-449 plan):
//   • Request classes — `short` (interactive JSON) fails fast and escalates
//     patience (1s → 2s → 4s); `long` (uploads / media) gets a single
//     generous ceiling. A short connect-timeout detects a shaped/blocked
//     path in ~1s instead of waiting out the browser's ~120s TCP timeout.
//   • RU is tried FOR SURE once the primary ladder is exhausted, and on the
//     first RU success we flip a shared, session-sticky `ruMode` so every
//     later request (and, via the same flag, SignalR + Stream) pre-routes to
//     RU without re-paying the failed-primary latency.
//   • Idempotency guard (CORE-315): a non-idempotent request that TIMED OUT
//     may have reached the server, so it is never retried/failed-over; a pure
//     transport error (never reached the server) still is.
//
// Everything here is web-only and gated behind NET_RESILIENCE_ENABLED — the
// legacy apiFallback path stays the default until this is validated on dev +
// the RU probe box.

import { Platform } from 'react-native';
import { hasIdempotencyKey } from './idempotencyKey';

/** Master flag — off until validated on dev + RU probe (CORE-449). */
export const NET_RESILIENCE_ENABLED =
  Platform.OS === 'web'
  && (process.env.EXPO_PUBLIC_NET_RESILIENCE === '1'
   || process.env.EXPO_PUBLIC_NET_RESILIENCE === 'true');

// ─── Shared "RU is better right now" sticky ──────────────────────────────
// Consulted by REST (apiFallback), SignalR hub URL resolution and Stream so a
// broadly-degraded RU network flips ALL of them to their RU hosts at once.
// Backed by localStorage (30-min TTL) so a reload keeps routing to RU instead
// of re-discovering the block; falls back to an in-memory flag in private mode.

const RU_MODE_KEY = 'net:ruModeAt';
const RU_MODE_TTL_MS = 30 * 60 * 1000; // 30 min
let ruModeMem = false;

/**
 * `Date.now()` is unavailable in some sandboxes (workflow scripts) but is
 * fine in the RN runtime; guard defensively so importing this module never
 * throws at load time.
 */
function nowMs(): number {
  try { return Date.now(); } catch { return 0; }
}

export function isRuMode(): boolean {
  if (ruModeMem) return true;
  try {
    const raw = window.localStorage?.getItem(RU_MODE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    if (nowMs() - ts < RU_MODE_TTL_MS) { ruModeMem = true; return true; }
  } catch { /* localStorage unavailable */ }
  return false;
}

export function setRuMode(): void {
  ruModeMem = true;
  try { window.localStorage?.setItem(RU_MODE_KEY, String(nowMs())); } catch { /* ignore */ }
}

export function clearRuMode(): void {
  ruModeMem = false;
  try { window.localStorage?.removeItem(RU_MODE_KEY); } catch { /* ignore */ }
}

// ─── Timeout ladders + request classification ────────────────────────────

export type RequestClass = 'short' | 'long';

/**
 * Per-attempt timeout ladder for a (class, idempotency) pair.
 *
 * Only IDEMPOTENT short requests get the aggressive fail-fast escalation
 * (1s → 2s → 4s): they are safe to replay, so aborting a slow-but-fine one
 * costs only a retry and lets a genuinely shaped path fail over in ~1s.
 *
 * A NON-idempotent short request must never be replayed after a timeout
 * (CORE-315 — a timed-out create may have reached the server), so a tight
 * first timeout would turn a legitimately slow write into a spurious failure
 * with no upside. Give it ONE generous attempt instead.
 *
 * `long` (uploads / media) always gets a single generous ceiling.
 */
export function timeoutLadder(cls: RequestClass, idempotent: boolean): number[] {
  if (cls === 'long') return [120_000];
  return idempotent ? [1000, 2000, 4000] : [15_000];
}

/**
 * A request is `long` (content / media / upload) when a short 1s timeout would
 * wrongly abort a legitimately slow transfer. Everything else is `short`.
 */
const LONG_PATTERNS: RegExp[] = [
  /\/presign(\?|$)/i,
  /\/content\/videos(\?|$)/i,
  /\/master\/content\/videos/i,
  /\/files\//i,
  /chunk/i,
  /\.(mp4|m3u8|mov|webm|jpe?g|png|webp|gif|mp3|wav|m4a)(\?|$)/i,
  /amazonaws\.com/i,
  /s3[.-]/i,
];

export function classifyRequest(url: string, method: string): RequestClass {
  const m = method.toUpperCase();
  // A body-bearing write to a content endpoint is an upload → long.
  if ((m === 'POST' || m === 'PUT') && /content|files|presign|upload/i.test(url)) return 'long';
  if (LONG_PATTERNS.some((re) => re.test(url))) return 'long';
  return 'short';
}

/** Safe to replay automatically after a timeout (may have hit the server). */
export function isIdempotent(method: string): boolean {
  return ['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
}

// ─── Error shape helpers ─────────────────────────────────────────────────

/**
 * DNS / TCP / TLS — usually. CORE-1910: in a browser a response WITHOUT CORS
 * headers (gateway 502/503/504) is the same `TypeError`, and it may arrive after
 * the server already executed the request — so it is NOT "always safe".
 */
export function isTransportError(err: unknown): boolean {
  return err instanceof TypeError;
}

/** Our own connect-timeout fired (DOMException name 'TimeoutError'). */
export function isTimeoutError(err: unknown): boolean {
  return typeof DOMException !== 'undefined'
    && err instanceof DOMException
    && err.name === 'TimeoutError';
}

/**
 * May we replay this failed attempt (on the primary's next ladder step, or by
 * failing over to RU)? Only when replaying cannot create a duplicate:
 * `replaySafe` = an idempotent method OR a request carrying Idempotency-Key
 * (CORE-1910 — the backend answers a repeat with the first response).
 *
 * CORE-1910 — a transport `TypeError` used to be retried for any method: it was
 * read as "never reached the server". A browser reports a CORS-less gateway
 * response the same way, possibly after the request was executed (CORE-1905),
 * so a POST without a key is no longer replayed on it either. A timeout follows
 * the same rule (CORE-315 — it may have reached the server): without a key never,
 * with a key the repeat gets the first response (or 409 while it still runs).
 */
export function canRetry(err: unknown, replaySafe: boolean): boolean {
  if (isTransportError(err) || isTimeoutError(err)) return replaySafe;
  return false;
}

// ─── Progressive fetch with RU failover ──────────────────────────────────

type FetchFn = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

function rebuildInput(
  input: RequestInfo | URL,
  fromUrl: string,
  toUrl: string,
): RequestInfo | URL {
  if (fromUrl === toUrl) return input;
  if (typeof input === 'string' || input instanceof URL) return toUrl;
  // Request object — clone against the new URL (preserves method/headers/body).
  return new Request(toUrl, input as Request);
}

/** One attempt against `targetUrl` with a hard connect/overall ceiling. */
async function attemptWithTimeout(
  originalFetch: FetchFn,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  fromUrl: string,
  targetUrl: string,
  timeoutMs: number,
): Promise<Response> {
  const ctrl = new AbortController();
  const callerSignal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
  const onCallerAbort = () => ctrl.abort((callerSignal as any)?.reason);
  if (callerSignal) {
    if (callerSignal.aborted) ctrl.abort((callerSignal as any)?.reason);
    else callerSignal.addEventListener('abort', onCallerAbort);
  }
  const timer = setTimeout(() => {
    ctrl.abort(new DOMException(
      `Запрос занял больше ${Math.round(timeoutMs / 1000)} с.`,
      'TimeoutError',
    ));
  }, timeoutMs);
  try {
    const rebuilt = rebuildInput(input, fromUrl, targetUrl);
    // `init.signal` wins over a Request's own signal per the fetch spec, so a
    // timeout retry correctly overrides a caller signal that already aborted.
    return await originalFetch(rebuilt, { ...(init || {}), signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
    if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
  }
}

/**
 * Run a request through the progressive ladder with a one-shot RU failover.
 *
 * `url` is the resolved primary URL; `toRu` rewrites it to the RU host.
 * `onRuSuccess` fires the first time an RU attempt succeeds (callers use it to
 * flip the shared sticky + any layer-specific breaker). Pure-code, injectable
 * `originalFetch` so it is unit-testable without a real network.
 */
export async function resilientFetch(opts: {
  originalFetch: FetchFn;
  input: RequestInfo | URL;
  init?: RequestInit;
  url: string;
  toRu: (u: string) => string;
  onRuSuccess?: () => void;
}): Promise<Response> {
  const { originalFetch, input, init, url, toRu, onRuSuccess } = opts;
  const method = (
    init?.method ?? (input instanceof Request ? input.method : 'GET')
  ).toUpperCase();
  const cls = classifyRequest(url, method);
  const idempotent = isIdempotent(method);
  const ladder = timeoutLadder(cls, idempotent);
  // CORE-1910 — с Idempotency-Key повтор не создаёт дубль: сервер отдаст ответ первого.
  const replaySafe = idempotent || hasIdempotencyKey(input, init);
  const ruUrl = toRu(url);

  // Already know RU is the better path this session → go straight there.
  if (isRuMode() && ruUrl !== url) {
    return attemptWithTimeout(originalFetch, input, init, url, ruUrl, ladder[ladder.length - 1]);
  }

  let lastErr: unknown;
  for (let i = 0; i < ladder.length; i++) {
    try {
      return await attemptWithTimeout(originalFetch, input, init, url, url, ladder[i]);
    } catch (err) {
      lastErr = err;
      if (!canRetry(err, replaySafe)) throw err; // real API error / cancel / unsafe timeout
    }
  }

  // Primary ladder exhausted — try RU once (only reached when canRetry held).
  if (ruUrl === url) throw lastErr; // no RU host for this URL (dev / non-primary)
  try {
    const res = await attemptWithTimeout(
      originalFetch, input, init, url, ruUrl, ladder[ladder.length - 1],
    );
    onRuSuccess?.();
    return res;
  } catch {
    throw lastErr; // surface the more useful primary error
  }
}
